import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { ProductContext } from "./provider.js";
import { useMediaQuery } from "../utilities/index.js";
import Image from "next/image";
import React from "react";
import useEmblaCarousel from "embla-carousel-react";
export const ProductImages = ({ mode = "vertical", isDesktop }) => {
    const { images = [] } = React.useContext(ProductContext);
    const [imgCounter, setImgCounter] = React.useState(0); // we use this counter for a image loading callback to reinit carousel
    const increaseImgCounterOnLoad = () => setImgCounter((curr) => curr + 1);
    const THUMBNAIL_SIZE = "72px";
    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.up("sm"));
    const isShowingThumbnail = isDesktop ?? isSm;
    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: "start",
        loop: true,
        skipSnaps: false,
        inViewThreshold: 0.7,
    });
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [_, setScrollSnaps] = React.useState([]);
    const scrollTo = React.useCallback((index) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);
    const onSelect = React.useCallback(() => {
        if (!emblaApi)
            return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi, setSelectedIndex]);
    React.useEffect(() => {
        if (!emblaApi || images.length === 0)
            return;
        emblaApi.reInit();
        onSelect();
        setScrollSnaps(emblaApi.scrollSnapList());
        emblaApi.on("select", onSelect);
    }, [emblaApi, images, setScrollSnaps, onSelect, imgCounter]);
    if (images.length === 0)
        return null;
    return (React.createElement(Stack, { direction: "column", spacing: { xs: 0, md: 2 }, sx: {
            mt: -2,
            overflow: "visible",
            pb: { xs: 2, md: 0 },
            position: "relative",
            width: "100%",
            zIndex: 30,
        } },
        React.createElement(Box, { className: "embla", id: "product-images-root", ref: emblaRef, sx: {
                maxWidth: { xs: "620px", lg: "unset" },
                margin: "0 auto",
                overflow: "hidden",
                mt: { xs: 0, sm: -3, md: 0 },
                mb: { xs: 0, sm: -3, md: 0 },
                ...(mode === "vertical" ? { display: "flex" } : {}),
            } },
            React.createElement(Box, { id: "product-images-container", className: "embla__container", sx: { display: "flex" } }, images.map(({ alt, image, mask }, ix) => (React.createElement(MaskedImage, { alt: alt, image: image, key: image.src, mask: mask, onLoad: increaseImgCounterOnLoad, priority: ix <= imgCounter }))))),
        images.length > 1 && !isShowingThumbnail && (React.createElement(Stack, { alignItems: "center", direction: "row", justifyContent: "center", spacing: 2 },
            React.createElement(IconButton, { onClick: () => scrollTo(selectedIndex - 1) },
                React.createElement(ChevronLeft, { sx: { fontSize: "14px" } })),
            React.createElement(Typography, { textAlign: "center", variant: "subtitle2", sx: { pt: 0, pb: 0 } },
                selectedIndex + 1,
                "\u00A0/\u00A0",
                images.length),
            React.createElement(IconButton, { onClick: () => scrollTo(selectedIndex + 1) },
                React.createElement(ChevronRight, { sx: { fontSize: "14px" } })))),
        images.length > 1 && isShowingThumbnail && (React.createElement(Stack, { direction: "row", spacing: { xs: 1, md: 1, lg: 2 }, sx: { justifyContent: "center", maxHeight: THUMBNAIL_SIZE } }, images.map(({ alt, image }, ix) => (React.createElement(Box, { "data-testid": "thumbnail", onClick: () => scrollTo(ix), key: `thumbnail-${image.src}`, sx: {
                "&:hover": { cursor: selectedIndex === ix ? "normal" : "pointer", opacity: 1.0 },
                border: `2px solid ${selectedIndex === ix ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.1)"}`,
                borderRadius: "6px",
                display: "flex",
                maxWidth: THUMBNAIL_SIZE,
                opacity: selectedIndex === ix ? 1.0 : 0.7,
                transition: theme.transitions.create(["opacity"]),
                width: "100%",
                "& > img": {
                    borderRadius: "6px",
                    overflow: "hidden",
                    width: "100%",
                    maxWidth: "100%",
                    height: "auto",
                },
            } },
            React.createElement(Image, { alt: alt, ...image, width: 70, height: 70 }))))))));
};
export const MaskedImage = ({ alt, image, mask, onLoad, priority = false, }) => {
    const [maskSrc, setMaskSrc] = React.useState("");
    const handleOnLoad = (a) => setMaskSrc(a.currentTarget.src);
    return (React.createElement(Box, { className: "embla__slide transition-all", key: image.src, sx: {
            "& > img": {
                borderRadius: "16px",
                overflow: "hidden",
                width: "100%",
                maxWidth: "100%",
                height: "auto",
            },
            borderRadius: "16px",
            display: "flex",
            flex: "0 0 100%",
            maskPosition: "center",
            maskRepeat: "no-repeat",
            maskSize: "cover",
            mr: 4,
            overflow: "hidden",
            ...(mask && maskSrc ? { maskImage: `url(${maskSrc})` } : {}),
        } },
        React.createElement(Image, { placeholder: "blur", priority: priority, alt: alt, ...image, onLoadingComplete: onLoad, style: { width: "100%", height: "auto" } }),
        mask && (React.createElement(Image, { ...mask, alt: alt, priority: priority, style: { display: "none" }, onLoad: handleOnLoad }))));
};
//# sourceMappingURL=images.js.map